import { Injectable } from '@angular/core';
import { BookmarkService, createBookmarkService } from 'fl-bookmarks';
import {
  ContentAuthorizationConfiguration,
  ContentAuthorizationData,
  ContentAuthorizer,
  createContentAuthorizer,
} from 'fl-content-authorization';
import { FavouriteService, createFavouriteService } from 'fl-favourites';
import { Error, isError } from 'fl-foundation';
import { HeartbeatBuilder, HeartbeatConfiguration, createHeartbeatBuilder } from 'fl-heartbeat';
import {
  AuthorizationData,
  PlatformAuthorizer,
  PlatformClient,
  PlatformConfig,
  UserAuthorizationData,
  UserAuthorizationDelegate,
  createPlatformAuthorizer,
  createPlatformClient,
} from 'fl-platform-authorizer';
import { ComposablePlayer, Player } from 'fl-player-interface';
import { getDeviceType } from 'qp-utility';
import { LocalStorageKeys } from '../../LocalStorageKeys';
import { GetPlatformPlayerModel } from '../../interfaces/player.interface';
import { isLoggedIn } from '../../utils/LoginUtils';
import {
  getComposablePlayer,
  getHeaders,
  getPlatformAsset,
  getPlatformPlayer,
  thumbnailsPlatformValidation,
} from '../../utils/PlaybackUtils';
import { getXClientId } from '../../utils/deviceUtils';
import { isLiveByMetadata, isResourceBookmark } from '../../utils/playerUtils';
import { saveAuthDataToken } from '../../utils/tokenUtils';
import { AppConfig, ConfigService } from '../config/config.service';
import { DeviceHandlerService } from '../device-handler/device-handler.service';
import { TokenService } from '../firstlight/guest-token/token.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { QPATService } from '../qpat/qpat.service';

@Injectable({
  providedIn: 'root',
})
export class PlaybackService {
  contentAuthorizer?: ContentAuthorizer;
  platformAuthorizer?: PlatformAuthorizer;
  bookmarkService?: BookmarkService;
  favoriteService?: FavouriteService;
  ovatToken: string;
  thumbnailSupported = false;
  private contentAuthConfiguration?: ContentAuthorizationConfiguration;
  private platformConfig?: PlatformConfig;
  private startTimePlayback = 0;
  private composablePlayer: ComposablePlayer;

  constructor(
    private configService: ConfigService,
    private deviceHandlerService: DeviceHandlerService,
    private tokenService: TokenService,
    private qpatService: QPATService,
    private localStorage: LocalStorageService
  ) {}

  set startTime(startTime: number) {
    this.startTimePlayback = startTime;
  }

  get startTime(): number {
    return this.startTimePlayback;
  }

  /**
   * set plarform config and content auth config
   *
   * @param applicationConfig
   */
  public intializeConfigurations(applicationConfig?: AppConfig): void {
    const appConfiguration = applicationConfig ? applicationConfig : this.configService.applicationConfig;
    this.platformConfig = {
      clientID: appConfiguration.clientID,
      clientSecret: appConfiguration.clientSecret,
      xClientId: getXClientId(),
      authorizationEndPoint: appConfiguration.oAuthURL,
    };
    this.contentAuthConfiguration = {
      endPointUrl: appConfiguration.contentAuthURL,
      clientRegistrationEndPointUrl: appConfiguration.clientRegURL,
    };
  }

  /**
   * Init SDK services
   * create platform authorizer
   * ensure authorization
   * save authdata in localstorage
   * init bookmarks, content authorizer, favorite service
   * set widevine certificate
   *
   * @returns Promise<AuthorizationData | Error>
   */
  async initialiseSDK(): Promise<AuthorizationData | Error> {
    this.intializeConfigurations();
    if (!this.platformAuthorizer) {
      this.createPlatformAuthoriser();
    }
    const authData: AuthorizationData | Error = await this.ensureAuthorization();
    this.initialiseOtherSDKServices();
    return Promise.resolve(authData);
  }

  /**
   * Call ensure authorization
   *
   * @returns Promise<AuthorizationData | Error>
   */
  async ensureAuthorization() {
    const authData: AuthorizationData | Error = await this.platformAuthorizer.ensureAuthorization();
    if (!isError(authData)) {
      saveAuthDataToken(authData, this.localStorage);
    }
    return authData;
  }

  /**
   * Authorize playback by content id and user auth
   * Call to createPlayerInstance
   */
  public async loadPlayerInstance(
    videoElement: HTMLVideoElement,
    uniqueId: string,
    metadataType: string,
    bookmarkcontentId: string = undefined,
    loadThumbnails = false,
    loadAds: boolean = false
  ): Promise<{ contentAuthorizationData: ContentAuthorizationData; playerInstance: Player }> {
    const platformAsset = getPlatformAsset(uniqueId, metadataType);
    const contentAuthorizationData = await this.contentAuthorizer.authorizePlayback(platformAsset, getHeaders());
    const seekPos = bookmarkcontentId ? await this.getSeekPosition(metadataType, uniqueId, bookmarkcontentId) : 0;
    const playerInstance = await this.createPlayer(
      videoElement,
      contentAuthorizationData,
      loadThumbnails,
      isLiveByMetadata(metadataType),
      loadAds,
      seekPos
    );
    this.buildHeartbeat(contentAuthorizationData, isLiveByMetadata(metadataType), playerInstance);
    return { contentAuthorizationData, playerInstance };
  }

  /**
   * Create player object
   *
   * @param playerConfig
   * @param contentAuthorizationData
   * @param checkforThumbnails
   * @param seekPos
   * @returns
   */
  private async createPlayer(
    videoElement: HTMLVideoElement,
    contentAuthorizationData: ContentAuthorizationData,
    checkforThumbnails: boolean,
    isLive: boolean,
    loadAds: boolean,
    seekPos: number = 0
  ): Promise<Player> {
    console.log('[PL] create player');
    console.log('[PL] load ads', loadAds);
    this.thumbnailSupported = checkforThumbnails ? this.thumbnailsValidation() : false;
    const getplatformPlayerModel: GetPlatformPlayerModel = {
      seekPos,
      applicationConfig: this.configService.applicationConfig,
      isLive,
      contentAuthorizationData,
      video: videoElement,
      showThumbnails: this.thumbnailSupported,
      thumbnailsUrl: undefined,
    };
    return getPlatformPlayer(getplatformPlayerModel);
  }

  /**
   * Check if the user is logged
   * If the user is logged, force the refresh call to qpat
   * if the user is not logged, force the refresh call to guest token
   *
   */
  public async refreshxAuthToken() {
    try {
      if (isLoggedIn(this.localStorage)) {
        this.localStorage.removeItem(LocalStorageKeys.XAUTH_TOKEN_EXP); //force to refresh
        await this.qpatService.refreshXAuthToken();
      } else {
        // guest token
        this.resetPlayback();
        await this.initialiseSDK();
        await this.contentAuthorizer.registerDevice();
      }
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * Validate if thumbnails are supported
   *
   * @returns
   */
  private thumbnailsValidation(): boolean {
    return !!this.configService?.applicationConfig?.enableKeyframes && thumbnailsPlatformValidation();
  }

  /**
   * Resets the playback by invalidating the token
   * */
  public resetPlayback(): void {
    if (this.platformAuthorizer) {
      this.platformAuthorizer.dispose();
    }
    if (this.contentAuthorizer) {
      this.contentAuthorizer.invalidateDeviceToken();
    }
  }

  async getAccessToken(): Promise<UserAuthorizationData | Error> {
    const ovatToken = this.localStorage.getItem(LocalStorageKeys.XAUTH_TOKEN);
    if (ovatToken === null || ovatToken === '') {
      if (!isLoggedIn(this.localStorage)) {
        const authResponse: AuthorizationData | Error = await this.platformAuthorizer.ensureAuthorization();
        if (!isError(authResponse)) {
          saveAuthDataToken(authResponse, this.localStorage);
          const result = await this.tokenService.fetchGuestToken(
            this.deviceHandlerService.getCurrentDeviceID(),
            getDeviceType(),
            authResponse
          );
          this.localStorage.setItem(LocalStorageKeys.XAUTH_TOKEN, result.token);
          return { accessToken: result.token };
        }
        return authResponse;
      }
    }
    return { accessToken: ovatToken };
  }

  private getAuthDelegate(): UserAuthorizationDelegate {
    const userAuthorizationDelegate: UserAuthorizationDelegate = {
      fetchUserAuthorizationToken: () => {
        return this.getAccessToken();
      },
    };
    return userAuthorizationDelegate;
  }

  private getPlatformClient(): PlatformClient {
    return createPlatformClient(this.deviceHandlerService.getCurrentDeviceID(), getDeviceType());
  }

  private createPlatformAuthoriser(): void {
    this.platformAuthorizer = createPlatformAuthorizer(this.platformConfig, this.getAuthDelegate());
  }

  private initialiseOtherSDKServices(): void {
    this.bookmarkService = createBookmarkService(this.configService.applicationConfig.bookmarkURL, this.platformAuthorizer);
    this.contentAuthorizer = createContentAuthorizer(this.contentAuthConfiguration, this.platformAuthorizer, this.getPlatformClient());
    this.favoriteService = createFavouriteService(this.configService.applicationConfig.favoriteURL, this.platformAuthorizer);
  }

  /**
   * Check if the composable player exists
   * if exist, return the insance
   * if not exist, return a new instance
   *
   * @returns Composable player
   */
  public getComposablePlayer(playerInstance: Player): ComposablePlayer {
    if (this.composablePlayer) {
      return this.composablePlayer;
    }
    return getComposablePlayer(playerInstance);
  }

  /**
   * Build hearbeat and attach to player
   *
   * @param authorizationResponse
   * @returns
   */
  public buildHeartbeat(authorizationResponse: ContentAuthorizationData, isLive: boolean, playerInstance: Player) {
    // Doesn't attach Heartbeat by authorization response flag
    if (!authorizationResponse.heartbeatFlag) {
      return;
    }
    // Heartbeat config
    const heartbeatConfiguration: HeartbeatConfiguration = {
      heartbeatEndPointUrl: this.configService.applicationConfig.heartBeatURL,
      syncIntervalMS: parseInt(this.configService.applicationConfig.heartBeatSyncInterval, 10),
    };

    // Building Heartbeat
    const heartbeatBuilder: HeartbeatBuilder = createHeartbeatBuilder(
      this.deviceHandlerService.getCurrentDeviceID(),
      authorizationResponse.contentId,
      heartbeatConfiguration,
      this.platformAuthorizer
    );
    const hBuilder = heartbeatBuilder.setHeartbeatToken(authorizationResponse.heartbeatToken);
    if (isLive) {
      hBuilder.setLiveEndTime(authorizationResponse.liveEndTime).setLiveStartTime(authorizationResponse.liveStartTime);
    }
    const heartbeatManager = hBuilder.build();
    // Attaches heartbeat to the player
    const composablePlayer: ComposablePlayer = this.getComposablePlayer(playerInstance);
    composablePlayer.addStateChangeStep((player: Player) => heartbeatManager.processPlayerStateChange(player));
    composablePlayer.addHeartBeatStep((player: Player) => heartbeatManager.processHeartbeatChange(player));
  }

  /**
   * Get seek position by bookmark
   *
   * @returns seek position
   */
  private async getSeekPosition(contentType: string, uniqueId: string, bookmarkcontentId: string): Promise<number> {
    try {
      if (isLoggedIn(this.localStorage)) {
        const response = await this.bookmarkService.getBookmark(bookmarkcontentId);
        if (isResourceBookmark(contentType, uniqueId, response)) {
          const seekPos = response.offset ? response.offset / 1000 : 0;
          return seekPos || 0;
        }
      }
      return 0;
    } catch (error) {
      return 0;
    }
  }
}
